import { useRef } from "react";

// Moving between fields can briefly focus two inputs in the same interaction, so
// hold the report and keep only the field focused last.
const FOCUS_DELAY = 250;

// Browser/OS autofill fills a field's whole value in one shot with no matching
// keystroke — Chrome tags it explicitly via inputType "insertReplacementText".
// Every other inputType the spec defines (typing, paste, drop, IME composition,
// delete, undo/redo, ...) is a real user action, so instead of maintaining our
// own list of every legitimate keystroke type, trust the browser's own
// classification and only name the one type that means autofill.
const AUTOFILL_INPUT_TYPES = new Set(["insertReplacementText"]);

export const isAutofillInput = (e) => {
  const inputType = e?.nativeEvent?.inputType;
  if (inputType) return AUTOFILL_INPUT_TYPES.has(inputType);
  // Some autofill implementations omit inputType entirely — fall back to: a
  // single typed character is a real keystroke, any other bulk single-shot
  // value isn't.
  const data = e?.nativeEvent?.data;
  return !(typeof data === "string" && data.length <= 1);
};

export const useFieldAnalytics = () => {
  const focusTimeout = useRef(null);
  const lastSelected = useRef(null);
  // Last value already reported per ENTERED event, so an autofill-triggered report
  // and a later blur on that same, unedited value don't double-fire.
  const lastReportedValue = useRef(new Map());

  const trackFieldSelected = (event) => {
    clearTimeout(focusTimeout.current);
    focusTimeout.current = setTimeout(() => {
      // Re-focusing the same field (a re-render, a label click) isn't a new selection.
      if (lastSelected.current === event) return;
      lastSelected.current = event;
      event();
    }, FOCUS_DELAY);
  };

  // Fires the ENTERED event once for a given value — from a blur (source
  // omitted) or from detected autofill (source: "autocomplete"). `value` is used
  // only to guard against empty fields and duplicate reports here; it is never
  // sent to the event function or the analytics payload.
  const trackFieldEntered = (event, value, source) => {
    if (!value || lastReportedValue.current.get(event) === value) return;
    lastReportedValue.current.set(event, value);
    event(source);
  };

  // Wire to a field's onInput. A real keystroke is left for onBlur to report;
  // a detected autofill fires immediately, tagged eventAttrs.source: "autocomplete".
  const trackAutofillEntered = (e, event) => {
    if (!isAutofillInput(e)) return;
    trackFieldEntered(event, e.target.value, "autocomplete");
  };

  return { trackFieldSelected, trackFieldEntered, trackAutofillEntered };
};
